import React from 'react';
import { muteShipmentNotificationsYourself } from '@/apis/shipment';
import { CustomTitle, CustomShipmentText } from '@/components';
import { ArrowLeftOutlined, CloseOutlined } from '@ant-design/icons';
import { Flex, message, Tooltip } from 'antd';
import { BellIcon, MuteBellIcon } from '@/assets/icons/icons';

interface ShipmentNotificationHeaderProps {
  toggleDrawer: (action: string | boolean, type?: string, id?: string) => void;
  shipmentId: string;
  muteNotification: boolean;
  setMuteNotification: React.Dispatch<React.SetStateAction<boolean>>;
  checkTokenExpiry: () => string;
  unauthorize: (error: unknown) => void;
  /** When provided, used for mute/unmute instead of internal API call (shared logic with parent). */
  onMuteToggle?: (muteForYourself: boolean) => void;
}

/**
 * Header component for shipment notifications drawer
 *
 * @param toggleDrawer - Function to control drawer state and navigation
 * @param shipmentId - ID of the current shipment
 * @param muteNotification - Current mute state for notifications
 * @param setMuteNotification - Function to update mute state
 * @param checkTokenExpiry - Function to get valid auth token
 * @param unauthorize - Function to handle authentication errors
 */
const ShipmentNotificationHeader: React.FC<ShipmentNotificationHeaderProps> = ({
  toggleDrawer,
  shipmentId,
  muteNotification,
  setMuteNotification,
  checkTokenExpiry,
  unauthorize,
  onMuteToggle,
}) => {
  const onToggleChange = (val: boolean): void => {
    if (onMuteToggle) {
      onMuteToggle(val);
      return;
    }
    const token = checkTokenExpiry();
    const model = {
      shipment: shipmentId,
      mute_for_yourself: val,
    };
    muteShipmentNotificationsYourself(token, model)
      .then((res) => {
        const data = res?.data || {};
        if (data?.data?.is_success) {
          setMuteNotification(data?.data?.data?.mute_status);
          message.success(data?.data?.message);
        } else {
          message.error(data?.data?.message);
        }
      })
      .catch((error: unknown) => {
        unauthorize(error);
      });
  };

  return (
    <>
      <div>
        <Flex
          align='center'
          justify='space-between'
          style={{ marginBottom: '8px' }}
        >
          <ArrowLeftOutlined
            onClick={() => toggleDrawer('open', 'Detail', shipmentId)}
            style={{ fontSize: '14px', cursor: 'pointer' }}
          />
          <Flex align='center' gap={8}>
            {muteNotification === false ? (
              <Tooltip
                title={'Mute notifications'}
                overlayInnerStyle={{ fontSize: 10 }}
              >
                <BellIcon
                  className='c-pointer'
                  onClick={() => onToggleChange(true)}
                />
              </Tooltip>
            ) : (
              <Tooltip
                title={'Unmute notifications'}
                overlayInnerStyle={{ fontSize: 10 }}
              >
                <MuteBellIcon
                  className='c-pointer'
                  onClick={() => onToggleChange(false)}
                />
              </Tooltip>
            )}

            <span
              title='Close'
              className='no-bg'
              style={{ padding: '4px', cursor: 'pointer' }}
              onClick={() => toggleDrawer(false, 'all')}
            >
              <CloseOutlined
                style={{ fontSize: '14px', color: 'var(--text-primary)' }}
              />
            </span>
          </Flex>
        </Flex>
        <div style={{ padding: '0px 6px' }}>
          <CustomTitle
            text='Shipment notifications'
            fontSize='20px'
            lineHeight='28px'
            fontWeight='700'
            marginBottom='4px'
            marginTop={'0px'}
          />
          <CustomShipmentText
            text='View all notifications that apply to this shipment set at the company level (inherited), and make shipment-specific ones'
            fontSize='14px'
            color='var(--text-secondary)'
            lineHeight='20px'
          />
        </div>
      </div>
    </>
  );
};

export default ShipmentNotificationHeader;
